/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import {
  Container, Typography, Box, Grid, CircularProgress, Chip,
  TextField, Button, MenuItem
} from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import SecurityIcon from '@mui/icons-material/Security';

const GOLD = '#d4af37';

interface OnlineDonationContent {
  enabled?: boolean;
  purposeLabel: string;
  purpose1: string;
  purpose2: string;
  purpose3: string;
  purpose4: string;
  purpose5: string;
}

export default function OnlineDonation() {
  const [content, setContent] = useState<OnlineDonationContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [donationEnabled, setDonationEnabled] = useState(true);
  const [amount, setAmount] = useState('500');
  const [customAmount, setCustomAmount] = useState('');
  const [purpose, setPurpose] = useState('General Fund');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  const predefinedAmounts = ['500', '1000', '2000', '5000', '10000'];

  const defaultContent: OnlineDonationContent = {
    purposeLabel: 'Purpose of Donation',
    purpose1: 'General Fund',
    purpose2: 'Annadanam (Food Distribution)',
    purpose3: 'Temple Renovation',
    purpose4: 'Festival Contribution',
    purpose5: 'Other'
  };

  useEffect(() => {
    const docRef = doc(db, 'online_donation_content', 'main');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      try {
        if (docSnap.exists()) {
          const data = docSnap.data() as OnlineDonationContent;
          setContent(data);
          setDonationEnabled(data.enabled !== false);
        } else {
          setContent(null);
        }
      } catch (error) {
        console.error('Error processing online donation content:', error);
      } finally {
        setLoading(false);
      }
    }, (error) => {
      console.error('Error setting up real-time listener:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const displayContent = content || defaultContent;

  const paymentMethods = [
    { Icon: PhoneAndroidIcon, label: 'UPI (Google Pay, PhonePe, Paytm)' },
    { Icon: CreditCardIcon, label: 'Credit / Debit Cards' },
    { Icon: AccountBalanceIcon, label: 'Net Banking' },
    { Icon: ReceiptLongIcon, label: '80G Tax Exemption Certificate Available' },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#3b82f6' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Online Donation — Kottayil Temple | ഓൺലൈൻ സംഭാവന" description="Make an online donation to Kottayil Temple (Kotta-Kovilakam Kshethram). Secure online payment for temple welfare, renovation, and development." path="/donate/online" keywords="online donation Kottayil temple, temple donation online, donate online Kerala temple, ഓൺലൈൻ സംഭാവന, കൊട്ടയിൽ ക്ഷേത്രം, secure temple payment" />
      <PageBanner title="Online Donation" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* ══════ VIBRANT HERO ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', mb: 3.5,
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 50%, #3b82f6 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.07, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3.5, sm: 4 }, pb: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Chip label="Secure Payment" size="small" icon={<SecurityIcon sx={{ fontSize: 14, color: '#fff !important' }} />} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.7rem', mb: 1, height: 24, backdropFilter: 'blur(8px)' }} />
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.5rem', sm: '1.8rem' }, color: '#fff', lineHeight: 1.2 }}>
                      Make a Donation
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, maxWidth: 600 }}>
                  Your generous contribution helps us maintain the temple and serve the community better. All transactions are secure and encrypted.
                </Typography>
              </Box>
            </Box>

            {/* ══════ DONATION DISABLED WARNING ══════ */}
            {!donationEnabled && (
              <Box sx={{
                mb: 3, borderRadius: '20px', overflow: 'hidden',
                border: '1.5px solid rgba(245,158,11,0.3)',
                bgcolor: '#fffbeb',
                boxShadow: '0 4px 20px rgba(245,158,11,0.08)',
              }}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  px: { xs: 2.5, sm: 3 }, py: 1.5,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                  <BlockIcon sx={{ fontSize: 20, color: '#fff' }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#fff' }}>
                    Online Donation Disabled
                  </Typography>
                </Box>
                <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2 }}>
                  <Typography sx={{ fontSize: '0.85rem', color: '#92400e', lineHeight: 1.7, fontWeight: 600 }}>
                    Online donation is currently not available. Please contact the temple office directly for making donations.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                    <WarningAmberIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                    <Typography sx={{ fontSize: '0.78rem', color: '#b45309', fontWeight: 500 }}>
                      Visit the Contact page or call the temple office for assistance.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* ══════ DONATION FORM CARD ══════ */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 3.5,
              ...(donationEnabled ? {} : { opacity: 0.45, pointerEvents: 'none', filter: 'grayscale(0.6)' }),
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                px: { xs: 2.5, sm: 3 }, py: 2,
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <PaymentIcon sx={{ fontSize: 20, color: '#fff' }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>
                  Donation Details
                </Typography>
              </Box>

              <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 2.5, pb: 3.5 }}>
                {/* Amount Selection */}
                <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#1e293b', mb: 2 }}>
                  Select Amount
                </Typography>
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                  {predefinedAmounts.map((amt) => (
                    <Grid item xs={4} sm={2.4} key={amt}>
                      <Box
                        onClick={() => { setAmount(amt); setCustomAmount(amt); }}
                        sx={{
                          py: 1.2, textAlign: 'center', borderRadius: '14px', cursor: 'pointer',
                          fontWeight: 800, fontSize: '0.9rem',
                          transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                          ...(amount === amt
                            ? {
                                background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                                color: '#fff', boxShadow: '0 6px 20px rgba(59,130,246,0.35)',
                                transform: 'scale(1.04)',
                              }
                            : {
                                bgcolor: '#f1f5f9', color: '#475569',
                                border: '1.5px solid rgba(0,0,0,0.06)',
                                '&:hover': { borderColor: 'rgba(59,130,246,0.3)', bgcolor: '#eff6ff', transform: 'translateY(-2px)' },
                              }
                          ),
                        }}
                      >
                        ₹{amt}
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Custom Amount */}
                <TextField
                  fullWidth label="Custom Amount (₹)" type="number"
                  value={customAmount}
                  onChange={(e) => { setCustomAmount(e.target.value); setAmount(''); }}
                  inputProps={{ min: '1' }}
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px', bgcolor: '#f8fafc',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                      '&:hover fieldset': { borderColor: 'rgba(59,130,246,0.4)' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1.5px' },
                    },
                    '& .MuiInputLabel-root': { fontSize: '0.82rem', '&.Mui-focused': { color: '#2563eb' } },
                  }}
                />

                {/* Purpose */}
                <TextField
                  select fullWidth
                  label={displayContent.purposeLabel}
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px', bgcolor: '#f8fafc',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                      '&:hover fieldset': { borderColor: 'rgba(59,130,246,0.4)' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1.5px' },
                    },
                    '& .MuiInputLabel-root': { fontSize: '0.82rem', '&.Mui-focused': { color: '#2563eb' } },
                  }}
                >
                  <MenuItem value={displayContent.purpose1}>{displayContent.purpose1}</MenuItem>
                  <MenuItem value={displayContent.purpose2}>{displayContent.purpose2}</MenuItem>
                  <MenuItem value={displayContent.purpose3}>{displayContent.purpose3}</MenuItem>
                  <MenuItem value={displayContent.purpose4}>{displayContent.purpose4}</MenuItem>
                  <MenuItem value={displayContent.purpose5}>{displayContent.purpose5}</MenuItem>
                </TextField>

                {/* Personal Info */}
                <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#1e293b', mb: 2, mt: 1 }}>
                  Your Details
                </Typography>

                <TextField
                  fullWidth label="Full Name *" type="text"
                  value={fullName} onChange={(e) => setFullName(e.target.value)}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px', bgcolor: '#f8fafc',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                      '&:hover fieldset': { borderColor: 'rgba(59,130,246,0.4)' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1.5px' },
                    },
                    '& .MuiInputLabel-root': { fontSize: '0.82rem', '&.Mui-focused': { color: '#2563eb' } },
                  }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth label="Email *" type="email"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '14px', bgcolor: '#f8fafc',
                          '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                          '&:hover fieldset': { borderColor: 'rgba(59,130,246,0.4)' },
                          '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1.5px' },
                        },
                        '& .MuiInputLabel-root': { fontSize: '0.82rem', '&.Mui-focused': { color: '#2563eb' } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth label="Phone Number *" type="tel"
                      value={phone} onChange={(e) => setPhone(e.target.value)}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '14px', bgcolor: '#f8fafc',
                          '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                          '&:hover fieldset': { borderColor: 'rgba(59,130,246,0.4)' },
                          '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1.5px' },
                        },
                        '& .MuiInputLabel-root': { fontSize: '0.82rem', '&.Mui-focused': { color: '#2563eb' } },
                      }}
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth label="Address" multiline rows={2}
                  value={address} onChange={(e) => setAddress(e.target.value)}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px', bgcolor: '#f8fafc',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                      '&:hover fieldset': { borderColor: 'rgba(59,130,246,0.4)' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1.5px' },
                    },
                    '& .MuiInputLabel-root': { fontSize: '0.82rem', '&.Mui-focused': { color: '#2563eb' } },
                  }}
                />

                {/* Submit */}
                {donationEnabled && (
                  <Button
                    fullWidth
                    endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
                    sx={{
                      background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                      color: '#fff', fontWeight: 800, fontSize: '0.95rem',
                      py: 1.6, borderRadius: '16px', textTransform: 'none',
                      boxShadow: '0 8px 28px rgba(59,130,246,0.35)',
                      transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      '&:hover': { transform: 'scale(1.02)', boxShadow: '0 12px 36px rgba(59,130,246,0.45)' },
                    }}
                  >
                    Proceed to Payment
                  </Button>
                )}
              </Box>
            </Box>

            {/* ══════ PAYMENT METHODS (Dark Slate Footer) ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              ...(donationEnabled ? {} : { opacity: 0.45, pointerEvents: 'none', filter: 'grayscale(0.6)' }),
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', bottom: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 3.5 }, py: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #e5c158)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <PaymentIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>
                    Payment Methods Accepted
                  </Typography>
                </Box>

                <Grid container spacing={1.5}>
                  {paymentMethods.map((m, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8 }}>
                        <Box sx={{
                          width: 36, height: 36, borderRadius: '10px',
                          bgcolor: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <m.Icon sx={{ fontSize: 16, color: '#60a5fa' }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
                          {m.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="donate" activePath="/donate/online" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
